import { useAtomValue, useSetAtom } from 'jotai';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useDismissOnMapClick, useLayerClick, useMap, useMapEvent } from '@/features/map';
import { useUnitFormatter } from '@/features/units';
import { selectStations, type Selection } from '../model/rank';
import type { Station } from '../model/types';
import { cancelStationsAtom, loadStationsAtom } from '../state/actions';
import {
  selectedStationAtom,
  selectedStationIdAtom,
  stationCacheAtom,
  stationsVisibilityAtom,
} from '../state/atoms';
import { arrowImage, MARKER_PIXEL_RATIO, MARKER_RADIUS } from './markerImage';
import { StationFader } from './stationFader';
import { StationPopup } from './StationPopup';
import {
  ARROW_IMAGE,
  STATION_CIRCLES_LAYER,
  STATION_LAYERS,
  STATIONS_SOURCE,
  type StationState,
} from './stationsStyle';
import { toFeatures } from './toFeatures';

/** Distance between drawn stations, px: a circle and its arrow plus some breathing room. */
const SPACING = 4.5 * MARKER_RADIUS;
/** Closest two stations may come while zooming out before they are spread out again: about touching. */
const MIN_SPACING = 2.75 * MARKER_RADIUS;
/** Stations are requested for the view plus this much of its size on every side. */
const PADDING = 0.25;
/** Observations are reloaded this often while shown, ms. */
const REFRESH_MS = 5 * 60_000;
/** A view needs new data after zooming this much since the last request. */
const RELOAD_ZOOM = 0.75;
/** Redraws while the camera moves are at most this frequent, ms. */
const REDRAW_MS = 150;

interface Bounds {
  w: number;
  s: number;
  e: number;
  n: number;
}

interface Request {
  bounds: Bounds;
  zoom: number;
  time: number;
}

/** Visible area grown by `pad` of its size on every side, clamped to the world. */
function paddedBounds(map: MapLibreMap, pad: number): Bounds {
  const b = map.getBounds();
  const dx = (b.getEast() - b.getWest()) * pad;
  const dy = (b.getNorth() - b.getSouth()) * pad;
  return {
    w: Math.max(-180, b.getWest() - dx),
    e: Math.min(180, b.getEast() + dx),
    s: Math.max(-85, b.getSouth() - dy),
    n: Math.min(85, b.getNorth() + dy),
  };
}

const contains = (outer: Bounds, inner: Bounds) =>
  inner.w >= outer.w && inner.e <= outer.e && inner.s >= outer.s && inner.n <= outer.n;

/** Stations to draw now: the cached ones in view, thinned to an even spread that changes little. */
function visibleStations(
  map: MapLibreMap,
  cache: ReadonlyMap<string, Station>,
  previous: Selection | null,
): Selection {
  const view = paddedBounds(map, 0.1);
  const { clientWidth: width, clientHeight: height } = map.getContainer();
  const inView = [...cache.values()].filter(
    (s) => s.lat >= view.s && s.lat <= view.n && s.lng >= view.w && s.lng <= view.e,
  );
  const margin = MARKER_RADIUS * 2;
  return selectStations({
    stations: inView,
    previous,
    zoom: map.getZoom(),
    spacing: SPACING,
    minSpacing: MIN_SPACING,
    project: (s) => {
      const p = map.project([s.lng, s.lat]);
      return p.x < -margin || p.y < -margin || p.x > width + margin || p.y > height + margin ? null : p;
    },
    now: Date.now(),
  });
}

/** Sets a boolean feature state on one station for as long as `id` is set. */
function useStationFlag(map: MapLibreMap | null, id: string | null, flag: 'hover' | 'selected') {
  useEffect(() => {
    if (!map || id === null) return;
    const target = { source: STATIONS_SOURCE, id };
    map.setFeatureState(target, { [flag]: true } satisfies StationState);
    return () => void (map.getSource(STATIONS_SOURCE) && map.removeFeatureState(target, flag));
  }, [map, id, flag]);
}

/**
 * Weather stations on the map: loads them for the view, draws an even spread that fades in and out as
 * it changes, emphasises the station under the mouse or with open details, opens details on tap.
 */
export function StationLayer() {
  const map = useMap();
  const visible = useAtomValue(stationsVisibilityAtom) === 'on';
  const cache = useAtomValue(stationCacheAtom);
  const load = useSetAtom(loadStationsAtom);
  const cancel = useSetAtom(cancelStationsAtom);
  const selectedId = useAtomValue(selectedStationIdAtom);
  const setSelected = useSetAtom(selectedStationIdAtom);
  const selected = useAtomValue(selectedStationAtom);
  const fmt = useUnitFormatter();
  const [hovered, setHovered] = useState<string | null>(null);
  const lastRequest = useRef<Request | null>(null);
  const fader = useRef<StationFader | null>(null);
  const selection = useRef<Selection | null>(null);

  useEffect(() => {
    if (!map) return;
    if (!map.hasImage(ARROW_IMAGE))
      map.addImage(ARROW_IMAGE, arrowImage(), { sdf: true, pixelRatio: MARKER_PIXEL_RATIO });
    const f = new StationFader(map);
    fader.current = f;
    return () => {
      f.dispose();
      fader.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    for (const id of STATION_LAYERS) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    if (!visible) {
      cancel();
      fader.current?.clear();
      lastRequest.current = null;
      selection.current = null;
    }
  }, [map, visible, cancel]);

  /** Requests stations unless the last request already covers the view at about this zoom. */
  const refresh = useEffectEvent((force = false) => {
    if (!map || !visible) return;
    const now = Date.now();
    const zoom = map.getZoom();
    const last = lastRequest.current;
    const covered =
      last &&
      now - last.time < REFRESH_MS &&
      Math.abs(zoom - last.zoom) < RELOAD_ZOOM &&
      contains(last.bounds, paddedBounds(map, 0));
    if (covered && !force) return;
    const bounds = paddedBounds(map, PADDING);
    const { clientWidth, clientHeight } = map.getContainer();
    lastRequest.current = { bounds, zoom, time: now };
    void load({
      bounds,
      width: clientWidth * (1 + 2 * PADDING),
      height: clientHeight * (1 + 2 * PADDING),
      spacing: SPACING,
    });
  });

  const redraw = useEffectEvent(() => {
    if (!map || !visible) return;
    selection.current = visibleStations(map, cache, selection.current);
    fader.current?.show(selection.current.stations, (stations) => toFeatures(stations, fmt));
  });

  useEffect(() => redraw(), [map, visible, cache, fmt]);

  useEffect(() => {
    if (!map || !visible) return;
    refresh();
    const timer = setInterval(() => document.visibilityState === 'visible' && refresh(true), REFRESH_MS);
    // Re-thin while the camera moves too, so stations fill in or make room as the scale changes.
    let redrawTimer: ReturnType<typeof setTimeout> | undefined;
    const onMove = () => {
      redrawTimer ??= setTimeout(() => {
        redrawTimer = undefined;
        redraw();
      }, REDRAW_MS);
    };
    const onMoveEnd = () => {
      clearTimeout(redrawTimer);
      redrawTimer = undefined;
      redraw();
      refresh();
    };
    map.on('move', onMove);
    map.on('moveend', onMoveEnd);
    return () => {
      clearInterval(timer);
      clearTimeout(redrawTimer);
      map.off('move', onMove);
      map.off('moveend', onMoveEnd);
    };
  }, [map, visible]);

  useLayerClick([STATION_CIRCLES_LAYER], (feature) => setSelected(String(feature.properties.id)));
  useDismissOnMapClick(selected !== null, () => setSelected(null));

  useMapEvent('mousemove', (e) => {
    const [feature] = e.target.getLayer(STATION_CIRCLES_LAYER)
      ? e.target.queryRenderedFeatures(e.point, { layers: [STATION_CIRCLES_LAYER] })
      : [];
    setHovered(feature ? String(feature.properties.id) : null);
  });
  useMapEvent('mouseout', () => setHovered(null));
  useStationFlag(map, hovered, 'hover');
  useStationFlag(map, selectedId, 'selected');

  return <StationPopup />;
}
